import React, { useCallback, useState } from "react";
import { commitMutation } from "react-relay";

import { faEllipsisV } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { ConnectionHandler, graphql, type RecordProxy } from "relay-runtime";

import CreateOrEditActionItemGroup from "~/actionItems/actionItemGroup/CreateOrEditActionItemGroup";
import type { GroupType } from "~/actionItems/types";
import { warning } from "~/lib/prompts";
import { environment } from "~/models";
import { useModals } from "~/ui";
import { IconButton } from "~/ui/legacyFellowIndex";
import Menu from "~/ui/menu";
import toasty from "~/ui/toast";

import useActionItemGroupItemsKey from "../hooks/useActionItemsGroupItemsKey";

import type { ActionItemGroupOptionsDeleteMutation } from "~/__generated__/ActionItemGroupOptionsDeleteMutation.graphql";

import styles from "./ActionItemGroupOptions.module.css";

const deleteActionItemGroupMutation = graphql`
	mutation ActionItemGroupOptionsDeleteMutation($groupId: ID!) {
		deleteCustomActionItemGroup(groupId: $groupId) {
			deletedGroupId
			actionItemGroups {
				id
				order
			}
		}
	}
`;

interface ActionItemGroupOptionsProps {
	group: GroupType;
}

export default function ActionItemGroupOptions({ group }: ActionItemGroupOptionsProps) {
	const itemsKey = useActionItemGroupItemsKey();
	const modals = useModals();

	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const toggleOpen = useCallback((open: boolean) => setIsMenuOpen(open), []);

	const renameGroup = useCallback(() => {
		modals.open(({ close }) => <CreateOrEditActionItemGroup onClose={close} group={group} itemsKey={itemsKey} />);
	}, [modals, group, itemsKey]);

	const deleteGroup = useCallback(() => {
		warning(
			<>
				Action items in <strong>{group.name}</strong> will be moved to inbox.
			</>,
			{
				yesProps: { variant: "destructive" },
				yesText: "Delete",
				noText: "Cancel",
				title: "Delete group?",
			},
		).then(res => {
			if (!res) return;
			commitMutation<ActionItemGroupOptionsDeleteMutation>(environment, {
				mutation: deleteActionItemGroupMutation,
				variables: { groupId: group.id },
				onCompleted: (response, error) => {
					if (error) {
						toasty.error("Failed to delete group.");
						console.error(error);
					}
				},
				updater: store => {
					const rootRecord = store.getRoot();
					if (!rootRecord) {
						return;
					}

					let defaultGroupItemsConnection: RecordProxy | undefined;
					let deletedGroupItems: Array<RecordProxy> | null | undefined;
					const connectionRecord = ConnectionHandler.getConnection(rootRecord, "ActionItemGroupsList_actionItemGroups");
					if (!connectionRecord) {
						return;
					}
					const actionItemGroups = (connectionRecord.getLinkedRecords("edges") ?? []).map(e =>
						e.getLinkedRecord("node"),
					);

					// Get default group and items from deleted group
					for (let i = 0; i < actionItemGroups.length; i++) {
						if (actionItemGroups[i]?.getValue("isDefaultGroup")) {
							defaultGroupItemsConnection = actionItemGroups[i]?.getLinkedRecord(itemsKey);
						} else if (actionItemGroups[i]?.getValue("id") === group.id) {
							const deletedGroupItemsConnections = actionItemGroups[i]?.getLinkedRecord(itemsKey);
							deletedGroupItems = deletedGroupItemsConnections?.getLinkedRecords("edges");
						}
						if (defaultGroupItemsConnection && deletedGroupItems) {
							break;
						}
					}
					if (!defaultGroupItemsConnection || !deletedGroupItems) {
						ConnectionHandler.deleteNode(connectionRecord, group.id);
						return;
					}
					deletedGroupItems?.forEach(deletedGroupItem => {
						// @ts-ignore
						ConnectionHandler.insertEdgeBefore(defaultGroupItemsConnection, deletedGroupItem);
					});
					ConnectionHandler.deleteNode(connectionRecord, group.id);
				},
			});
		});
	}, [group, itemsKey]);

	const createGroup = useCallback(() => {
		modals.open(({ close }) => <CreateOrEditActionItemGroup onClose={close} order={group.order} itemsKey={itemsKey} />);
	}, [modals, group, itemsKey]);

	return (
		<Menu open={isMenuOpen} onOpenChange={toggleOpen}>
			<Menu.Trigger>
				<IconButton icon={faEllipsisV} className={styles.ellipsis}></IconButton>
			</Menu.Trigger>
			<Menu.Content align="end">
				<Menu.Item onSelect={renameGroup}>Rename…</Menu.Item>
				{!group.isDefaultGroup && (
					<Menu.Item onSelect={deleteGroup} destructive>
						Delete
					</Menu.Item>
				)}
				<Menu.Separator />
				<Menu.Item onSelect={createGroup}>Create new group</Menu.Item>
			</Menu.Content>
		</Menu>
	);
}
